import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import type { AllocationView } from '@lims/shared-types';
import { invoicesApi, paymentsApi } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  ConfirmDialog,
  DataTable,
  Dialog,
  ErrorState,
  LoadingState,
  NumberField,
  Panel,
  SelectField,
  StatusBadge,
  TextAreaField,
  newRowKey,
  patchRow,
  useToast,
  withoutRow,
  type DataColumn,
} from '../../components/ui/index.js';
import { IconRemove } from '../../components/ui/nav-icons.js';
import { formatDate, formatMoney } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import {
  ContextItem,
  Facts,
  RecordGrid,
  RecordHeader,
  RecordLink,
  RecordNumber,
} from '../clients/record-layout.js';
import { FinanceMutationError } from './finance-errors.js';

interface AllocationDraft {
  /** The row's own key — never its position (see components/ui/rows.ts). */
  readonly key: string;
  invoiceId: string | null;
  amount: number | null;
}

/** A recorded payment: what it settled, and the two acts that can follow. */
export function PaymentDetailPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { show } = useToast();
  const locale = currentLocale();

  const [allocateOpen, setAllocateOpen] = useState(false);
  const [drafts, setDrafts] = useState<AllocationDraft[]>([]);
  const [reversing, setReversing] = useState<AllocationView | null>(null);
  const [allocationReason, setAllocationReasonState] = useState('');
  const [reversePaymentOpen, setReversePaymentOpen] = useState(false);
  const [reason, setReason] = useState('');

  const payment = useQuery({
    queryKey: ['payment', id],
    queryFn: () => paymentsApi.get(id),
    enabled: Boolean(id),
  });

  const openInvoices = useQuery({
    queryKey: ['invoices', 'open', payment.data?.client.id],
    queryFn: () =>
      invoicesApi.list({
        clientId: payment.data?.client.id as string,
        status: 'issued',
        pageSize: 100,
      }),
    enabled: allocateOpen && Boolean(payment.data?.client.id),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['payment', id] });
    await queryClient.invalidateQueries({ queryKey: ['payments'] });
    await queryClient.invalidateQueries({ queryKey: ['invoices'] });
  };

  const allocate = useMutation({
    mutationFn: () =>
      paymentsApi.allocate(id, {
        allocations: drafts
          .filter((draft) => draft.invoiceId && draft.amount)
          .map((draft) => ({
            invoiceId: draft.invoiceId as string,
            amountMinor: Math.round((draft.amount as number) * 100),
          })),
      }),
    onSuccess: async () => {
      await invalidate();
      setAllocateOpen(false);
      setDrafts([]);
      show({ tone: 'success', message: t('payments.allocated') });
    },
  });

  const reverseAllocation = useMutation({
    mutationFn: () =>
      paymentsApi.reverseAllocation(
        id,
        (reversing as AllocationView).id,
        { reason: allocationReason.trim() },
      ),
    onSuccess: async () => {
      await invalidate();
      setReversing(null);
      setAllocationReasonState('');
      show({ tone: 'success', message: t('invoices.allocationReversed') });
    },
  });

  const reversePayment = useMutation({
    mutationFn: () => paymentsApi.reverse(id, { reason: reason.trim() }),
    onSuccess: async () => {
      await invalidate();
      setReversePaymentOpen(false);
      setReason('');
      show({ tone: 'success', message: t('payments.reversed') });
    },
  });

  if (payment.isLoading) return <LoadingState label={t('common.loading')} />;
  if (payment.error) {
    return <ErrorState error={payment.error} onRetry={() => void payment.refetch()} />;
  }
  const record = payment.data;
  if (!record) return null;

  const money = (minor: number) => formatMoney(minor, { locale, currency: record.currency });
  const usedInvoiceIds = new Set(
    record.allocations.filter((a) => !a.reversal).map((a) => a.invoiceId),
  );
  const availableInvoices = (openInvoices.data?.data ?? []).filter(
    (invoice) => invoice.balanceMinor > 0 && !usedInvoiceIds.has(invoice.id),
  );

  const allocationColumns: readonly DataColumn<AllocationView>[] = [
    {
      key: 'invoice',
      header: t('invoices.title'),
      render: (allocation) => (
        <RecordNumber to={`/invoices/${allocation.invoiceId}`}>
          {allocation.invoiceNumber}
        </RecordNumber>
      ),
    },
    {
      key: 'amount',
      header: t('invoices.amount'),
      align: 'end',
      render: (allocation) => (
        <span dir="ltr" className="measure">
          {money(allocation.amountMinor)}
        </span>
      ),
    },
    {
      key: 'when',
      header: t('common.recordedAt'),
      render: (allocation) => (
        <span className="text-default-600">
          {formatDate(allocation.allocatedAt, { locale })}
          {allocation.allocatedBy.displayName
            ? ` — ${allocation.allocatedBy.displayName}`
            : ''}
        </span>
      ),
    },
    {
      key: 'reversal',
      header: t('common.status'),
      align: 'end',
      render: (allocation) =>
        allocation.reversal ? (
          <StatusBadge label={t('invoices.reversed')} tone="warning" />
        ) : can(PERMISSIONS.PAYMENTS_ADJUST) && record.status === 'recorded' ? (
          <ActionButton
            priority="tertiary"
            size="sm"
            onPress={() => setReversing(allocation)}
          >
            {t('invoices.reverseAllocation')}
          </ActionButton>
        ) : null,
    },
  ];

  const clientLabel = record.client.name ?? record.client.number;
  const amount = (minor: number, strong = false) => (
    <span dir="ltr" className={`measure${strong ? ' font-semibold' : ''}`}>
      {money(minor)}
    </span>
  );

  return (
    <>
      <RecordHeader
        badges={
          <StatusBadge
            label={t(`status.paymentRecord.${record.status}`)}
            tone={record.status === 'reversed' ? 'warning' : 'success'}
          />
        }
        title={t('payments.paymentTitle', { client: clientLabel })}
        context={
          <>
            <ContextItem label={t('payments.client')}>
              <RecordLink to={`/clients/${record.client.id}`}>{clientLabel}</RecordLink>
            </ContextItem>
            <ContextItem label={t('payments.receivedOn')}>
              {formatDate(record.receivedOn, { locale })}
            </ContextItem>
          </>
        }
        breadcrumbs={[
          { label: t('nav.payments'), to: '/payments' },
          { label: t('payments.paymentTitle', { client: record.client.number }) },
        ]}
        actions={
          <ActionBar>
            {record.can.allocate && can(PERMISSIONS.PAYMENTS_RECORD) ? (
              <ActionButton priority="secondary" onPress={() => setAllocateOpen(true)}>
                {t('payments.allocateMore')}
              </ActionButton>
            ) : null}
            {record.can.reverse && can(PERMISSIONS.PAYMENTS_ADJUST) ? (
              <ActionButton
                priority="destructive"
                onPress={() => setReversePaymentOpen(true)}
              >
                {t('payments.reversePayment')}
              </ActionButton>
            ) : null}
          </ActionBar>
        }
      />

      <div className="mb-stack">
        <FinanceMutationError
          error={allocate.error ?? reverseAllocation.error ?? reversePayment.error}
        />
      </div>

      {record.reversal ? (
        <div className="mb-stack">
          <Alert
            tone="warning"
            title={t('payments.reversedNotice')}
            description={`${record.reversal.reason} — ${formatDate(record.reversal.at, { locale })}`}
          />
        </div>
      ) : null}

      <RecordGrid
        side={
          <>
            <Panel title={t('common.details')}>
              <Facts
                items={[
                  {
                    label: t('payments.client'),
                    value: (
                      <RecordLink to={`/clients/${record.client.id}`}>{clientLabel}</RecordLink>
                    ),
                  },
                  {
                    label: t('payments.receivedOn'),
                    value: formatDate(record.receivedOn, { locale }),
                  },
                  {
                    label: t('payments.methodLabel'),
                    value: t(`paymentMethod.${record.method}`),
                  },
                  {
                    label: t('payments.reference'),
                    value: record.reference ? (
                      <span dir="ltr" className="measure">
                        {record.reference}
                      </span>
                    ) : (
                      t('common.notRecorded')
                    ),
                  },
                  {
                    label: t('common.recordedAt'),
                    value: `${formatDate(record.recordedAt, { locale }) ?? ''}${
                      record.recordedBy.displayName ? ` — ${record.recordedBy.displayName}` : ''
                    }`,
                  },
                  record.note
                    ? {
                        label: t('payments.note'),
                        value: (
                          <span className="whitespace-pre-line" dir="auto">
                            {record.note}
                          </span>
                        ),
                      }
                    : null,
                ]}
              />
            </Panel>
          </>
        }
      >
        {/* The money: received, settled against invoices, and what remains
            on the client's account. The server's figures, never recomputed. */}
        <Panel>
          <dl className="grid grid-cols-1 gap-x-gutter gap-y-2 rounded-medium bg-default-50 px-3 py-2 sm:grid-cols-3">
            {(
              [
                [t('payments.amount'), record.amountMinor, true],
                [t('payments.allocated'), record.allocatedMinor, false],
                [t('payments.unallocated'), record.unallocatedMinor, false],
              ] as const
            ).map(([label, minor, strong]) => (
              <div key={label} className="min-w-0">
                <dt className="text-caption text-default-600">{label}</dt>
                <dd className="text-body text-default-900">{amount(minor, strong)}</dd>
              </div>
            ))}
          </dl>
        </Panel>

        <Panel title={t('payments.allocations')}>
          <DataTable
            label={t('payments.allocations')}
            columns={allocationColumns}
            rows={record.allocations}
            rowKey={(allocation) => allocation.id}
            emptyContent={t('invoices.noAllocations')}
          />
        </Panel>
      </RecordGrid>

      <Dialog
        isOpen={allocateOpen}
        onClose={() => setAllocateOpen(false)}
        title={t('payments.allocateMore')}
        footer={
          <ActionBar>
            <ActionButton priority="tertiary" onPress={() => setAllocateOpen(false)}>
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isLoading={allocate.isPending}
              isDisabled={!drafts.some((draft) => draft.invoiceId && draft.amount)}
              onPress={() => allocate.mutate()}
            >
              {t('common.save')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="grid gap-stack">
          <Alert tone="info" title={t('payments.allocationsHint')} />
          {drafts.map((draft, index) => (
            // One block per allocation: its place and its removal in a header
            // strip, the invoice and amount beneath (D278).
            <div
              key={draft.key}
              className="min-w-0 rounded-medium border border-default-200"
              data-testid="allocation-row"
            >
              <div className="flex min-h-10 items-center gap-inline border-b border-default-200 bg-default-50 px-3 py-1.5">
                <span className="text-caption font-semibold text-default-800">
                  {t('ordersRecord.lineNumber', { n: index + 1 })}
                </span>
                {/* Nothing is saved until Save, so a row goes at once, unconfirmed. */}
                <span className="ms-auto">
                  <ActionButton
                    priority="destructive"
                    size="sm"
                    startContent={<IconRemove size={16} />}
                    onPress={() => setDrafts((current) => withoutRow(current, draft.key))}
                  >
                    {t('payments.removeAllocation')}
                  </ActionButton>
                </span>
              </div>
              <div className="grid min-w-0 grid-cols-1 gap-stack p-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                <SelectField
                  label={t('payments.invoice')}
                  value={draft.invoiceId}
                  onChange={(value) =>
                    setDrafts((current) => patchRow(current, draft.key, { invoiceId: value }))
                  }
                  options={availableInvoices.map((invoice) => ({
                    value: invoice.id,
                    label: invoice.invoiceNumber ?? invoice.id,
                    description:
                      formatMoney(invoice.balanceMinor, {
                        locale,
                        currency: invoice.currency,
                      }) ?? undefined,
                  }))}
                />
                <NumberField
                  label={t('payments.allocationAmount')}
                  value={draft.amount}
                  onChange={(value) =>
                    setDrafts((current) => patchRow(current, draft.key, { amount: value }))
                  }
                  step="0.01"
                  unit={record.currency}
                />
              </div>
            </div>
          ))}
          <ActionButton
            priority="secondary"
            size="sm"
            onPress={() =>
              setDrafts((current) => [
                ...current,
                { key: newRowKey('allocation'), invoiceId: null, amount: null },
              ])
            }
          >
            {t('payments.addAllocation')}
          </ActionButton>
        </div>
      </Dialog>

      <ConfirmDialog
        isOpen={reversing !== null}
        onClose={() => setReversing(null)}
        title={t('invoices.reverseAllocationTitle')}
        message={t('invoices.reverseAllocationMessage')}
        confirmLabel={t('invoices.reverseAllocation')}
        confirmPriority="destructive"
        isPending={reverseAllocation.isPending}
        onConfirm={() => reverseAllocation.mutate()}
      >
        <TextAreaField
          label={t('common.reason')}
          value={allocationReason}
          onChange={setAllocationReasonState}
          isRequired
          rows={2}
        />
      </ConfirmDialog>

      <ConfirmDialog
        isOpen={reversePaymentOpen}
        onClose={() => setReversePaymentOpen(false)}
        title={t('payments.reversePaymentTitle')}
        message={t('payments.reversePaymentMessage')}
        confirmLabel={t('payments.reversePayment')}
        confirmPriority="destructive"
        isPending={reversePayment.isPending}
        onConfirm={() => reversePayment.mutate()}
      >
        <TextAreaField
          label={t('common.reason')}
          value={reason}
          onChange={setReason}
          isRequired
          rows={3}
        />
      </ConfirmDialog>
    </>
  );
}
